import { useEffect, useState } from "react";
import { Save, RotateCcw, Plus, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useContentCtx } from "@/context/ContentContext";

// ---------- field helpers ----------
const Field = ({ label, hint, children }) => (
    <div className="mb-5">
        <label className="label-mono">{label}</label>
        {children}
        {hint && <div className="mt-1 text-[11px] text-gray-500">{hint}</div>}
    </div>
);

const Text = ({ value, onChange, placeholder, testid }) => (
    <input className="input-field" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} />
);

const Area = ({ value, onChange, rows = 3, placeholder, testid }) => (
    <textarea className="input-field" rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} />
);

const SectionCard = ({ title, subtitle, children }) => (
    <section className="bg-white border border-gray-200 mb-8" data-testid={`section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
        <div className="p-6 md:p-8 border-b border-gray-100">
            <div className="overline text-red-600 mb-2">{title}</div>
            {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
        </div>
        <div className="p-6 md:p-8">{children}</div>
    </section>
);

// ---------- editors for arrays ----------
const StatsEditor = ({ items, onChange }) => {
    const update = (i, key, val) => {
        const arr = [...items];
        arr[i] = { ...arr[i], [key]: val };
        onChange(arr);
    };
    const add = () => onChange([...items, { value: "", label: "" }]);
    const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

    return (
        <div className="space-y-3" data-testid="editor-stats">
            {items.map((s, i) => (
                <div key={i} className="grid grid-cols-[1fr_2fr_auto] gap-2" data-testid={`stat-row-${i}`}>
                    <input className="input-field" value={s.value || ""} onChange={(e) => update(i, "value", e.target.value)} placeholder="Value (e.g. 30)" />
                    <input className="input-field" value={s.label || ""} onChange={(e) => update(i, "label", e.target.value)} placeholder="Label (e.g. Countries Shipped)" />
                    <button type="button" onClick={() => remove(i)} className="p-3 border border-gray-200 hover:bg-gray-100"><X size={16} /></button>
                </div>
            ))}
            <button type="button" onClick={add} className="btn-outline" data-testid="add-stat"><Plus size={14} /> Add Stat</button>
        </div>
    );
};

const TripleEditor = ({ items, onChange, fields = ["title", "body", "icon"], addLabel = "Add Item" }) => {
    const update = (i, key, val) => {
        const arr = [...items];
        arr[i] = { ...arr[i], [key]: val };
        onChange(arr);
    };
    const add = () => onChange([...items, Object.fromEntries(fields.map((f) => [f, ""]))]);
    const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

    return (
        <div className="space-y-4">
            {items.map((it, i) => (
                <div key={i} className="border border-gray-200 p-4">
                    <div className="flex justify-between items-start mb-3">
                        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500">Item {i + 1}</div>
                        <button type="button" onClick={() => remove(i)} className="p-1.5 hover:bg-gray-100"><X size={14} /></button>
                    </div>
                    {fields.includes("title") && (
                        <input className="input-field mb-2" value={it.title || ""} onChange={(e) => update(i, "title", e.target.value)} placeholder="Title" />
                    )}
                    {fields.includes("body") && (
                        <textarea className="input-field mb-2" rows={2} value={it.body || ""} onChange={(e) => update(i, "body", e.target.value)} placeholder="Body" />
                    )}
                    {fields.includes("icon") && (
                        <input className="input-field" value={it.icon || ""} onChange={(e) => update(i, "icon", e.target.value)} placeholder="Icon name (Cog, Layers, Zap, Award, Globe2, ShieldCheck, Factory, Wrench)" />
                    )}
                </div>
            ))}
            <button type="button" onClick={add} className="btn-outline"><Plus size={14} /> {addLabel}</button>
        </div>
    );
};

const StringListEditor = ({ items, onChange, placeholder = "Item" }) => {
    const update = (i, v) => {
        const arr = [...items]; arr[i] = v; onChange(arr);
    };
    const add = () => onChange([...items, ""]);
    const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
    return (
        <div className="space-y-2">
            {items.map((v, i) => (
                <div key={i} className="flex gap-2">
                    <input className="input-field flex-1" value={v} onChange={(e) => update(i, e.target.value)} placeholder={placeholder} />
                    <button type="button" onClick={() => remove(i)} className="p-3 border border-gray-200 hover:bg-gray-100"><X size={16} /></button>
                </div>
            ))}
            <button type="button" onClick={add} className="btn-outline"><Plus size={14} /> Add</button>
        </div>
    );
};

const ProcessFlowsEditor = ({ flows, onChange }) => {
    const updateTitle = (i, val) => {
        const arr = [...flows]; arr[i] = { ...arr[i], title: val }; onChange(arr);
    };
    const updateSteps = (i, steps) => {
        const arr = [...flows]; arr[i] = { ...arr[i], steps }; onChange(arr);
    };
    const addFlow = () => onChange([...flows, { title: "New Process", steps: [""] }]);
    const removeFlow = (i) => onChange(flows.filter((_, idx) => idx !== i));
    const move = (i, dir) => {
        const j = i + dir;
        if (j < 0 || j >= flows.length) return;
        const arr = [...flows]; [arr[i], arr[j]] = [arr[j], arr[i]]; onChange(arr);
    };
    return (
        <div className="space-y-5" data-testid="editor-processes">
            {flows.map((f, i) => (
                <div key={i} className="border border-gray-200 p-5" data-testid={`flow-${i}`}>
                    <div className="flex items-center justify-between gap-3 mb-4">
                        <div className="flex items-center gap-2 flex-1">
                            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500 whitespace-nowrap">Flow {i + 1}</span>
                            <input
                                className="input-field flex-1"
                                value={f.title || ""}
                                onChange={(e) => updateTitle(i, e.target.value)}
                                placeholder="Flow title (e.g. Outer Shell Production)"
                                data-testid={`flow-title-${i}`}
                            />
                        </div>
                        <div className="flex gap-1">
                            <button type="button" onClick={() => move(i, -1)} className="px-2 py-2 border border-gray-200 hover:bg-gray-100 text-xs">↑</button>
                            <button type="button" onClick={() => move(i, 1)} className="px-2 py-2 border border-gray-200 hover:bg-gray-100 text-xs">↓</button>
                            <button type="button" onClick={() => removeFlow(i)} className="p-2 border border-gray-200 hover:bg-red-50 text-red-600"><X size={14} /></button>
                        </div>
                    </div>
                    <div className="overline text-gray-500 mb-2">Steps (in order)</div>
                    <StringListEditor
                        items={f.steps || []}
                        onChange={(v) => updateSteps(i, v)}
                        placeholder="Step name (e.g. Pipe cutting)"
                    />
                </div>
            ))}
            <button type="button" onClick={addFlow} className="btn-outline" data-testid="add-flow"><Plus size={14} /> Add Flow</button>
        </div>
    );
};

// ---------- main page ----------
const AdminContent = () => {
    const ctx = useContentCtx();
    const [c, setC] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/content").then(({ data }) => setC(data));
    }, []);

    if (!c) {
        return <div className="font-mono text-xs uppercase tracking-[0.3em] text-gray-500" data-testid="content-loading">Loading content…</div>;
    }

    // helper: set nested key
    const set = (path, value) => {
        const next = structuredClone(c);
        const parts = path.split(".");
        let node = next;
        for (let i = 0; i < parts.length - 1; i++) node = node[parts[i]] = node[parts[i]] || {};
        node[parts[parts.length - 1]] = value;
        setC(next);
    };
    const setSection = (section, key, value) => {
        setC({ ...c, [section]: { ...(c[section] || {}), [key]: value } });
    };

    const save = async () => {
        setBusy(true);
        try {
            await api.put("/content", c);
            ctx.setContent(c);
            toast.success("Content saved. Public site updated.");
        } catch (e) {
            toast.error("Save failed: " + (e.response?.data?.detail || ""));
        } finally {
            setBusy(false);
        }
    };

    const reset = async () => {
        if (!window.confirm("Reset all content to defaults? This cannot be undone.")) return;
        setBusy(true);
        try {
            const { data } = await api.post("/content/reset");
            setC(data);
            ctx.setContent(data);
            toast.success("Content reset to defaults.");
        } catch {
            toast.error("Reset failed.");
        } finally {
            setBusy(false);
        }
    };

    const header = c.header || {};
    const hero = c.hero || {};
    const stats = c.stats || [];
    const cap = c.capabilities || {};
    const featured = c.featured || {};
    const cats = c.categories_section || {};
    const why = c.why || {};
    const bottom = c.bottom_cta || {};
    const about = c.about || {};
    const contact = c.contact || {};
    const footer = c.footer || {};

    return (
        <div data-testid="admin-content-page" className="pb-32">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
                <div>
                    <div className="overline text-red-600 mb-3">Site Content</div>
                    <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl">Edit Site Text</h1>
                    <p className="text-sm text-gray-600 mt-3 max-w-2xl">
                        All text on the public site is editable here. Wrap any word with <code className="bg-gray-100 px-1.5 py-0.5 font-mono text-[11px]">**word**</code> to display it in the red accent color (e.g. <code className="bg-gray-100 px-1.5 py-0.5 font-mono text-[11px]">**Solutions**</code>).
                    </p>
                </div>
                <div className="flex gap-3">
                    <a href="/" target="_blank" rel="noreferrer" className="btn-outline" data-testid="content-preview">
                        Preview Site <ExternalLink size={14} />
                    </a>
                    <button onClick={reset} disabled={busy} className="btn-outline" data-testid="content-reset"><RotateCcw size={14} /> Reset</button>
                    <button onClick={save} disabled={busy} className="btn-primary" data-testid="content-save"><Save size={14} /> {busy ? "Saving…" : "Save"}</button>
                </div>
            </div>

            {/* HEADER */}
            <SectionCard title="Header" subtitle="Top navigation bar — labels and CTA button.">
                <div className="grid md:grid-cols-2 gap-x-6">
                    <Field label="Quote CTA Button"><Text value={header.cta_label} onChange={(v) => setSection("header", "cta_label", v)} testid="header-cta-label" /></Field>
                    <Field label="Nav: Home"><Text value={header.nav_home} onChange={(v) => setSection("header", "nav_home", v)} /></Field>
                    <Field label="Nav: Catalog"><Text value={header.nav_catalog} onChange={(v) => setSection("header", "nav_catalog", v)} /></Field>
                    <Field label="Nav: About"><Text value={header.nav_about} onChange={(v) => setSection("header", "nav_about", v)} /></Field>
                    <Field label="Nav: Contact"><Text value={header.nav_contact} onChange={(v) => setSection("header", "nav_contact", v)} /></Field>
                </div>
            </SectionCard>

            {/* HERO */}
            <SectionCard title="Home — Hero" subtitle="The first thing visitors see on the homepage.">
                <Field label="Overline (small label above title)"><Text value={hero.overline} onChange={(v) => setSection("hero", "overline", v)} testid="hero-overline" /></Field>
                <Field label="Main Headline" hint="Use line breaks for separate lines. Wrap a word in **word** to highlight in orange.">
                    <Area value={hero.headline} onChange={(v) => setSection("hero", "headline", v)} rows={5} testid="hero-headline" />
                </Field>
                <Field label="Description Paragraph"><Area value={hero.description} onChange={(v) => setSection("hero", "description", v)} rows={3} testid="hero-description" /></Field>
                <div className="grid md:grid-cols-2 gap-x-6">
                    <Field label="Primary Button"><Text value={hero.primary_cta} onChange={(v) => setSection("hero", "primary_cta", v)} testid="hero-primary-cta" /></Field>
                    <Field label="Secondary Button"><Text value={hero.secondary_cta} onChange={(v) => setSection("hero", "secondary_cta", v)} testid="hero-secondary-cta" /></Field>
                </div>
            </SectionCard>

            {/* STATS */}
            <SectionCard title="Home — Stats Strip" subtitle="Four metrics displayed below the hero (e.g. Countries Shipped).">
                <StatsEditor items={stats} onChange={(v) => setC({ ...c, stats: v })} />
            </SectionCard>

            {/* PROCESS FLOWS */}
            <SectionCard title="Home — Bottle Manufacturing Process" subtitle="Line-art flow diagrams shown after the stats. Each flow is a titled list of process steps.">
                <Field label="Overline"><Text value={(c.process || {}).overline} onChange={(v) => setSection("process", "overline", v)} testid="process-overline" /></Field>
                <Field label="Heading" hint="Wrap word in **word** for red highlight."><Text value={(c.process || {}).heading} onChange={(v) => setSection("process", "heading", v)} testid="process-heading" /></Field>
                <Field label="Paragraph"><Area value={(c.process || {}).paragraph} onChange={(v) => setSection("process", "paragraph", v)} rows={3} /></Field>

                <div className="hairline my-6" />

                <div className="overline text-gray-500 mb-3">Process Flows</div>
                <ProcessFlowsEditor flows={(c.process || {}).flows || []} onChange={(v) => setSection("process", "flows", v)} />
            </SectionCard>

            {/* CAPABILITIES */}
            <SectionCard title="Home — Capabilities" subtitle="What you make / do, shown as a 4-column grid.">
                <Field label="Overline"><Text value={cap.overline} onChange={(v) => setSection("capabilities", "overline", v)} /></Field>
                <Field label="Heading"><Text value={cap.heading} onChange={(v) => setSection("capabilities", "heading", v)} /></Field>
                <Field label="Paragraph"><Area value={cap.paragraph} onChange={(v) => setSection("capabilities", "paragraph", v)} /></Field>
                <div className="mt-4">
                    <div className="overline text-gray-500 mb-3">Capability Items</div>
                    <TripleEditor items={cap.items || []} onChange={(v) => setSection("capabilities", "items", v)} addLabel="Add Capability" />
                </div>
            </SectionCard>

            {/* FEATURED */}
            <SectionCard title="Home — Featured Machinery" subtitle="Heading shown above the featured products grid.">
                <div className="grid md:grid-cols-3 gap-x-6">
                    <Field label="Overline"><Text value={featured.overline} onChange={(v) => setSection("featured", "overline", v)} /></Field>
                    <Field label="Heading"><Text value={featured.heading} onChange={(v) => setSection("featured", "heading", v)} /></Field>
                    <Field label="View-All Link Label"><Text value={featured.view_all} onChange={(v) => setSection("featured", "view_all", v)} /></Field>
                </div>
            </SectionCard>

            {/* CATEGORIES */}
            <SectionCard title="Home — Categories Section">
                <div className="grid md:grid-cols-2 gap-x-6">
                    <Field label="Overline"><Text value={cats.overline} onChange={(v) => setSection("categories_section", "overline", v)} /></Field>
                    <Field label="Heading"><Text value={cats.heading} onChange={(v) => setSection("categories_section", "heading", v)} /></Field>
                </div>
            </SectionCard>

            {/* WHY */}
            <SectionCard title="Home — Why Choose Us" subtitle="Dark section with 4 reasons / value propositions.">
                <Field label="Overline"><Text value={why.overline} onChange={(v) => setSection("why", "overline", v)} /></Field>
                <Field label="Heading"><Text value={why.heading} onChange={(v) => setSection("why", "heading", v)} /></Field>
                <div className="mt-4">
                    <div className="overline text-gray-500 mb-3">Reasons</div>
                    <TripleEditor items={why.items || []} onChange={(v) => setSection("why", "items", v)} addLabel="Add Reason" />
                </div>
            </SectionCard>

            {/* BOTTOM CTA */}
            <SectionCard title="Home — Bottom CTA">
                <Field label="Overline"><Text value={bottom.overline} onChange={(v) => setSection("bottom_cta", "overline", v)} /></Field>
                <Field label="Heading" hint="Line breaks allowed."><Area value={bottom.heading} onChange={(v) => setSection("bottom_cta", "heading", v)} rows={2} /></Field>
                <Field label="Button"><Text value={bottom.button} onChange={(v) => setSection("bottom_cta", "button", v)} /></Field>
            </SectionCard>

            {/* ABOUT */}
            <SectionCard title="About Page" subtitle="Everything on the /about page.">
                <Field label="Hero Overline"><Text value={about.hero_overline} onChange={(v) => setSection("about", "hero_overline", v)} /></Field>
                <Field label="Hero Heading" hint="Use **word** for orange highlight."><Text value={about.hero_heading} onChange={(v) => setSection("about", "hero_heading", v)} /></Field>
                <Field label="Hero Paragraph"><Area value={about.hero_paragraph} onChange={(v) => setSection("about", "hero_paragraph", v)} /></Field>

                <div className="hairline my-6" />

                <Field label="Approach Overline"><Text value={about.approach_overline} onChange={(v) => setSection("about", "approach_overline", v)} /></Field>
                <Field label="Approach Heading"><Text value={about.approach_heading} onChange={(v) => setSection("about", "approach_heading", v)} /></Field>
                <Field label="Approach Body — Paragraph 1"><Area value={about.approach_body1} onChange={(v) => setSection("about", "approach_body1", v)} rows={4} /></Field>
                <Field label="Approach Body — Paragraph 2"><Area value={about.approach_body2} onChange={(v) => setSection("about", "approach_body2", v)} rows={4} /></Field>

                <div className="hairline my-6" />

                <div className="overline text-gray-500 mb-3">Features (3 columns)</div>
                <TripleEditor items={about.features || []} onChange={(v) => setSection("about", "features", v)} addLabel="Add Feature" />

                <div className="hairline my-6" />

                <Field label="CTA Overline"><Text value={about.cta_overline} onChange={(v) => setSection("about", "cta_overline", v)} /></Field>
                <Field label="CTA Heading"><Text value={about.cta_heading} onChange={(v) => setSection("about", "cta_heading", v)} /></Field>
                <Field label="CTA Button"><Text value={about.cta_button} onChange={(v) => setSection("about", "cta_button", v)} /></Field>
            </SectionCard>

            {/* CONTACT */}
            <SectionCard title="Contact Page" subtitle="Everything on the /contact page (excluding the inquiry form fields themselves).">
                <Field label="Hero Overline"><Text value={contact.hero_overline} onChange={(v) => setSection("contact", "hero_overline", v)} /></Field>
                <Field label="Hero Heading" hint="Use **word** for orange highlight."><Text value={contact.hero_heading} onChange={(v) => setSection("contact", "hero_heading", v)} /></Field>
                <Field label="Hero Paragraph"><Area value={contact.hero_paragraph} onChange={(v) => setSection("contact", "hero_paragraph", v)} /></Field>

                <div className="hairline my-6" />

                <div className="grid md:grid-cols-2 gap-x-6">
                    <Field label="Email Label"><Text value={contact.email_label} onChange={(v) => setSection("contact", "email_label", v)} /></Field>
                    <Field label="Email Address"><Text value={contact.email} onChange={(v) => setSection("contact", "email", v)} testid="contact-email-value" /></Field>
                    <Field label="Phone Label"><Text value={contact.phone_label} onChange={(v) => setSection("contact", "phone_label", v)} /></Field>
                    <Field label="Phone Number"><Text value={contact.phone} onChange={(v) => setSection("contact", "phone", v)} testid="contact-phone-value" /></Field>
                    <Field label="Phone Link (tel:…)"><Text value={contact.phone_link} onChange={(v) => setSection("contact", "phone_link", v)} /></Field>
                    <Field label="Operations Label"><Text value={contact.operations_label} onChange={(v) => setSection("contact", "operations_label", v)} /></Field>
                    <Field label="Operations / Location"><Text value={contact.operations} onChange={(v) => setSection("contact", "operations", v)} /></Field>
                </div>

                <div className="hairline my-6" />

                <Field label="Lead Times Heading"><Text value={contact.lead_times_title} onChange={(v) => setSection("contact", "lead_times_title", v)} /></Field>
                <Field label="Lead Times List">
                    <StringListEditor items={contact.lead_times || []} onChange={(v) => setSection("contact", "lead_times", v)} placeholder="e.g. Single machines: 30–60 days" />
                </Field>

                <div className="hairline my-6" />

                <Field label="Success — Title (after inquiry sent)"><Text value={contact.success_title} onChange={(v) => setSection("contact", "success_title", v)} /></Field>
                <Field label="Success — Body"><Area value={contact.success_body} onChange={(v) => setSection("contact", "success_body", v)} /></Field>
            </SectionCard>

            {/* FOOTER */}
            <SectionCard title="Footer" subtitle="Dark footer at the bottom of every page.">
                <Field label="Description Paragraph"><Area value={footer.description} onChange={(v) => setSection("footer", "description", v)} rows={3} /></Field>
                <div className="grid md:grid-cols-3 gap-x-6">
                    <Field label="Column Heading: Catalog"><Text value={footer.catalog_heading} onChange={(v) => setSection("footer", "catalog_heading", v)} /></Field>
                    <Field label="Column Heading: Company"><Text value={footer.company_heading} onChange={(v) => setSection("footer", "company_heading", v)} /></Field>
                    <Field label="Column Heading: Contact"><Text value={footer.contact_heading} onChange={(v) => setSection("footer", "contact_heading", v)} /></Field>
                    <Field label="Email"><Text value={footer.email} onChange={(v) => setSection("footer", "email", v)} /></Field>
                    <Field label="Phone"><Text value={footer.phone} onChange={(v) => setSection("footer", "phone", v)} /></Field>
                    <Field label="Location"><Text value={footer.location} onChange={(v) => setSection("footer", "location", v)} /></Field>
                </div>
                <Field label="Copyright Tagline (right side of bottom bar)"><Text value={footer.copyright_tagline} onChange={(v) => setSection("footer", "copyright_tagline", v)} /></Field>
            </SectionCard>

            {/* Sticky save bar */}
            <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-gray-200 p-4 z-30 md:left-[260px]">
                <div className="max-w-4xl mx-auto flex justify-end gap-3">
                    <button onClick={reset} disabled={busy} className="btn-outline"><RotateCcw size={14} /> Reset to Defaults</button>
                    <button onClick={save} disabled={busy} className="btn-primary"><Save size={14} /> {busy ? "Saving…" : "Save All Changes"}</button>
                </div>
            </div>
        </div>
    );
};

export default AdminContent;
